import { ColorMatrixFilter, Container, Sprite, Text, type Texture, type TextStyleOptions } from 'pixi.js';
import { FRIEND_GAME_LABEL, friendAlias, friendOf, gameAlias, type RoadLamp } from '@goodtime/shared';
import { glowTexture } from './glow';
import { COLOURS, displayStyle, uiStyle } from '../board/ui';

/** Looks a texture up by alias (the scene passes AssetLoader's). */
export type ArtLookup = (alias: string) => Texture | null;

/** Gold display type with a dark outline (the kit's display style). */
export function displayText(value: string, size: number, color: number = COLOURS.gold): Text {
  const t = new Text({ text: value, style: { ...displayStyle(size, color), align: 'center' } });
  t.anchor.set(0.5, 0);
  return t;
}

/** Plain words on wood, wrapped to `width` (the kit's UI style). */
export function uiText(value: string, size: number, width: number, extra: Partial<TextStyleOptions> = {}): Text {
  const t = new Text({
    text: value,
    style: {
      ...uiStyle(size, 0xffffff, '600'),
      align: 'center',
      wordWrap: true,
      wordWrapWidth: width,
      lineHeight: Math.round(size * 1.3),
      ...extra,
    },
  });
  t.anchor.set(0.5, 0);
  return t;
}

/** A sprite `size` across (keeping its aspect), centred on its anchor, or
 *  null while the art hasn't loaded. */
export function artSprite(art: ArtLookup, alias: string, size: number): Sprite | null {
  const texture = art(alias);
  if (!texture) return null;
  const s = new Sprite(texture);
  s.anchor.set(0.5);
  const k = size / Math.max(texture.width, texture.height, 1);
  s.scale.set(k);
  return s;
}

/** The lens dimmed for a dark lamp, shared by every dark lamp. */
let unlit: ColorMatrixFilter | null = null;
function unlitFilter(): ColorMatrixFilter {
  if (!unlit) {
    unlit = new ColorMatrixFilter();
    unlit.desaturate();
    unlit.brightness(0.45, true);
  }
  return unlit;
}

/**
 * One lamp as the panel and the explanations draw it, `size` across and
 * centred on (0, 0): the brass lens, and in it the prize, a lit friend's
 * game, or (dark) the friend's shadow under a lock. A Wheel Run lamp (a game
 * and no friend) shows its game once lit, and a lock while dark (the panel
 * draws the stars it needs under it). A lit lamp glows.
 */
export function lampFace(lamp: RoadLamp, size: number, art: ArtLookup, glow = true): Container {
  const face = new Container();
  const friend = friendOf(lamp);
  if (lamp.lit && glow) {
    const glow = new Sprite(glowTexture());
    glow.anchor.set(0.5);
    glow.width = glow.height = size * 1.7;
    glow.tint = 0xffd25a;
    glow.alpha = 0.55;
    glow.blendMode = 'add';
    face.addChild(glow);
  }
  const lens = artSprite(art, 'road:lamp_lens', size);
  if (lens) {
    if (!lamp.lit) lens.filters = [unlitFilter()];
    face.addChild(lens);
  }
  const add = (s: Sprite | null) => (s ? face.addChild(s) : null);
  if (friend && lamp.lit) {
    add(artSprite(art, gameAlias(friend.game), size * 0.66));
  } else if (!friend && lamp.game && lamp.lit) {
    add(artSprite(art, gameAlias(lamp.game), size * 0.66));
  } else if (friend) {
    const who = artSprite(art, friendAlias(friend.id), size * 0.7);
    if (who) {
      who.tint = 0x000000;
      who.alpha = 0.55;
      face.addChild(who);
    }
    const lock = artSprite(art, 'road:lock', size * 0.3);
    if (lock) {
      lock.position.set(size * 0.32, size * 0.32);
      face.addChild(lock);
    }
  } else if (!lamp.lit) {
    add(artSprite(art, 'road:lock', size * 0.36));
  } else if (lamp.id === 'free_spins') {
    add(artSprite(art, 'shared:icon:spin', size * 0.46));
  } else if (lamp.id === 'silver') {
    add(artSprite(art, 'road:pot_silver', size * 0.5));
  } else {
    add(artSprite(art, 'shared:icon:coin', size * 0.46));
  }
  return face;
}

/** What a lamp's label says: its prize, a lit friend's game, or (dark)
 *  whose lamp it is. A Wheel Run lamp says its game once lit; dark, it has
 *  no words (the stars it needs are drawn instead). */
export function lampLabel(lamp: RoadLamp): string {
  const friend = friendOf(lamp);
  if (friend) return lamp.lit ? FRIEND_GAME_LABEL[friend.game] : friend.short;
  if (lamp.game) return lamp.lit ? FRIEND_GAME_LABEL[lamp.game] : '';
  return lamp.lit ? (lamp.label ?? '') : '?';
}

/**
 * The stars a dark Wheel Run lamp needs, as a row of `count` star pictures
 * (`icon/star.png`, never a glyph: phones can't be trusted to draw one),
 * `size` each, centred on x = 0 with its top at y = 0. Empty while the art
 * hasn't loaded.
 */
export function starRow(count: number, size: number, art: ArtLookup, lit = true): Container {
  const row = new Container();
  const gap = Math.max(1, Math.round(size * 0.12));
  const width = count * size + (count - 1) * gap;
  for (let i = 0; i < count; i++) {
    const s = artSprite(art, 'road:star', size);
    if (!s) continue;
    s.position.set(-width / 2 + size / 2 + i * (size + gap), size / 2);
    if (!lit) {
      s.filters = [unlitFilter()];
      s.alpha = 0.7;
    }
    row.addChild(s);
  }
  return row;
}
